import { useState, useMemo, useEffect, useContext, useRef } from 'react';
import { v4 } from 'uuid';
import { Input, Button, Refreshing, Modal, cn, Badge, Spinner } from '@aidkitorg/component-library';
import { markdownToHtml } from '@aidkitorg/typesheets/lib/richtexteditor';
import type { SupportedLanguage } from '@aidkitorg/i18n/lib';
import { toMarkdownWithReplacements } from '../Hooks/ModularMarkdown';
import { langToWord, useLocalizedStrings } from '../Localization';
import { LoggedInConfigurationContext, UserInfoContext } from '../Context';
import { InfoDict } from '../Questions/Props';
import { usePost } from '../API';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { useDebouncedMemo } from '../Hooks/Debounce';
import { checkPageAccess } from '@aidkitorg/types/lib/admin-panel/permissions';
import type { Directory } from '@aidkitorg/aidkit/lib/directory';
import type { SupportTemplateInsertion } from '@aidkitorg/aidkit/lib/application/communicate';
import type { extractReturnAsJson } from '@aidkitorg/types/lib/endpoints';

export type SupportTemplate = extractReturnAsJson<Directory['/api/admin_panel/support/templates/explore']>[number];
export type { SupportTemplateInsertion };

/** Which composer the picker is embedded in, recorded on the audit entry. */
export type SupportTemplateSurface = 'support_case' | 'applicant_comms' | 'text_entry';

// Characters that sit flush against a link without needing a space inserted:
// whitespace plus the opening/closing punctuation that normally wraps one.
const HUGS_LINK_START = /[\s([{<"'‘“]/;
const HUGS_LINK_END = /[\s.,;:!?)\]}>"'’”]/;

// Strips HTML tags from a template body while preserving the surrounding text
// (and any markdown), so `<span>hello</span>` becomes `hello`. Block-level
// boundaries (`<br>`, `</p>`, `</div>`, list items, headings, table rows) are
// converted to newlines first — otherwise `textContent` would collapse
// `<p>a</p><p>b</p>` into `ab`. Mirrors the HTML handling in
// `stripHtmlAndMarkdown`, but keeps markdown intact. Parsing via the DOM also
// decodes entities (`&amp;` -> `&`).
//
// List items keep a plain-text marker (`- ` or `1. `, indented by nesting
// depth) rather than flattening to bare lines, so an ordered list's numbering
// survives into SMS.
//
// Anchors need special care: turndown's `link-with-target` rule keeps links
// with a `target` attribute as raw `<a href="…">label</a>`, and bare
// `textContent` would drop the href, leaving a CTA with no URL (irrecoverable
// on email). So each anchor is rewritten to `label (href)` — the same
// convention `stripHtmlAndMarkdown` uses for markdown links — before stripping.
function stripHtmlTags(input: string): string {
  const withBreaks = input
    .replace(/<br\s*\/?>/gi, '\n')
    // `</li>` keeps its tag (the list pass below needs the tree to tell an
    // `<ol>` item from a `<ul>` one) but drops the whitespace a formatter puts
    // after it, which would otherwise land as a blank line between items. Every
    // other closer keeps its trailing whitespace — that newline between `</p>`
    // and `<p>` is what makes the blank line between paragraphs.
    .replace(/(<\/li>)\s+/gi, '$1')
    // Same on the inside: marked closes a nested list as `</ul>\n</li>`, and
    // that newline would open a blank line before the parent list resumes.
    .replace(/\s+(<\/li>)/gi, '$1')
    .replace(/<\/(?:p|div|h[1-6]|blockquote|tr)>/gi, '\n');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  // Re-attach a text marker to each list item. Flattening `<ol><li>` to bare
  // lines would silently drop the step numbers off an ordered list, turning
  // "do these in order" instructions into an unsequenced pile.
  for (const item of Array.from(doc.querySelectorAll('li'))) {
    const list = item.parentElement;
    const ordered = list?.tagName === 'OL';
    // `children` is elements only, so for a well-formed list this is the item's
    // ordinal. `start` honours `<ol start="3">`, which marked emits for a
    // markdown list that doesn't begin at 1.
    const position = list ? Array.from(list.children).indexOf(item) : 0;
    const start = Number(list?.getAttribute('start') ?? 1) || 1;
    let depth = 0;
    for (let p = item.parentElement; p; p = p.parentElement) {
      if (p.tagName === 'OL' || p.tagName === 'UL') depth++;
    }
    const marker = ordered ? `${start + position}. ` : '- ';
    item.prepend(doc.createTextNode('  '.repeat(Math.max(0, depth - 1)) + marker));
    // An item ending in a nested list already got its newline from that list's
    // last item; appending another would open a blank line mid-list.
    const endsWithNestedList = ['OL', 'UL'].includes(item.lastElementChild?.tagName ?? '');
    if (!endsWithNestedList) item.append(doc.createTextNode('\n'));
  }
  for (const anchor of Array.from(doc.querySelectorAll('a[href]'))) {
    const href = anchor.getAttribute('href')?.trim() ?? '';
    const label = (anchor.textContent ?? '').trim();
    // Keep just the href when there's no distinct label (e.g. an image-only
    // anchor, or an auto-link whose label already is the URL) to avoid
    // "url (url)" noise; otherwise surface both.
    const replacement = !href || !label || label === href ? (href || label) : `${label} (${href})`;
    // Anchors are inline, so the block-boundary pass above puts no separator
    // between a link and text butted right up against it (`</a>If you have…`
    // would strip to `…fillable.pdfIf you have…`). Pad only where the neighbour
    // isn't already whitespace or punctuation that legitimately hugs the link,
    // so `(<a>docs</a>).` doesn't gain spaces inside the parens.
    const prevChar = anchor.previousSibling?.textContent?.slice(-1) ?? '';
    const nextChar = anchor.nextSibling?.textContent?.slice(0, 1) ?? '';
    const padded = !replacement ? '' : [
      prevChar && !HUGS_LINK_START.test(prevChar) ? ' ' : '',
      replacement,
      nextChar && !HUGS_LINK_END.test(nextChar) ? ' ' : '',
    ].join('');
    anchor.replaceWith(doc.createTextNode(padded));
  }
  const text = doc.body.textContent ?? '';
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Shared "/"-triggered support-template picker state. Owns enablement, the
 * template list fetch, the popover-open trigger (a lone "/" at the start of the
 * message or after whitespace), and the preview-modal selection. Consumers wire
 * the returned state into a <Popover> around their composer input and render a
 * <SupportTemplatePreviewModal>. The trailing "/" is stripped by the consumer
 * (composers differ — plain string vs. markdown+html), so this hook only
 * surfaces the setters.
 */
export function useSupportTemplatePicker(currentMessage: string) {
  const loggedInConfig = useContext(LoggedInConfigurationContext);
  const userInfo = useContext(UserInfoContext);
  const exploreSupportTemplates = usePost('/api/admin_panel/support/templates/explore');

  const [supportTemplates, setSupportTemplates] = useState<SupportTemplate[]>([]);
  const [isTemplatePopoverOpen, setIsTemplatePopoverOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<SupportTemplate | null>(null);

  const supportTemplatesEnabled = !!(
    loggedInConfig?.comms?.supportTemplates
    && !loggedInConfig.comms.supportTemplates.disabled
    && checkPageAccess(userInfo?.tags, 'support-templates', 'view')
  );

  useAsyncEffect(async (signal) => {
    if (!supportTemplatesEnabled) return;
    const result = await exploreSupportTemplates({ include_archived: false }, signal);
    if (signal.aborted) return;
    if (!result || 'error' in result) return;
    setSupportTemplates(result);
  }, [supportTemplatesEnabled]);

  useEffect(() => {
    if (supportTemplates.length === 0) {
      setIsTemplatePopoverOpen(false);
      return;
    }
    setIsTemplatePopoverOpen(/(^|\s)\/$/.test(currentMessage));
  }, [currentMessage, supportTemplates]);

  return {
    supportTemplatesEnabled,
    supportTemplates,
    isTemplatePopoverOpen,
    setIsTemplatePopoverOpen,
    previewTemplate,
    setPreviewTemplate,
  };
}

export function SupportTemplateSelector({ supportTemplates, onSelectTemplate }: {
  supportTemplates: SupportTemplate[];
  onSelectTemplate: (template: SupportTemplate) => void;
}) {
  const L = useLocalizedStrings();
  const UNCATEGORIZED_KEY = '__uncategorized__';
  const exploreSupportTemplates = usePost('/api/admin_panel/support/templates/explore');
  const [searchQuery, setSearchQuery] = useState('');
  // Ranked server-side matches and the query that produced them; null when there is no query.
  const [searchResults, setSearchResults] = useState<{ query: string, templates: SupportTemplate[] } | null>(null);
  const [searching, setSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

  const debouncedQuery = useDebouncedMemo(() => searchQuery.trim(), 200, [searchQuery]);
  useAsyncEffect(async (signal) => {
    if (!debouncedQuery) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const result = await exploreSupportTemplates({ include_archived: false, query: debouncedQuery }, signal);
      if (signal.aborted || !result || 'error' in result) return;
      setSearchResults({ query: debouncedQuery, templates: result });
    } finally {
      // An aborted run was superseded by a newer one, which owns the indicator now.
      if (!signal.aborted) setSearching(false);
    }
  }, [debouncedQuery]);

  // Checked against the live query so clearing the input returns to the grouped list without waiting out the debounce.
  const liveQuery = searchQuery.trim();
  const visibleResults = liveQuery ? searchResults?.templates ?? null : null;
  // Until results for the live query arrive (debounce + request), the rows on screen belong to an
  // older query or the unfiltered list; they stay visible but can't be selected.
  const resultsAreStale = !!liveQuery && liveQuery !== searchResults?.query;

  // A query shows one flat, ranked section (null category); otherwise templates are grouped by category.
  const sections = useMemo((): [string | null, SupportTemplate[]][] => {
    if (visibleResults) return [[null, visibleResults]];
    const groups = new Map<string, SupportTemplate[]>();
    for (const template of supportTemplates) {
      const categories = template.categories.length > 0 ? template.categories : [UNCATEGORIZED_KEY];
      for (const category of categories) {
        const existing = groups.get(category);
        if (existing) {
          existing.push(template);
        } else {
          groups.set(category, [template]);
        }
      }
    }
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === UNCATEGORIZED_KEY) return 1;
      if (b === UNCATEGORIZED_KEY) return -1;
      return a.localeCompare(b);
    });
  }, [supportTemplates, visibleResults]);

  const flatItems = useMemo(
    () => sections.flatMap(([category, templates]) =>
      templates.map(template => ({ category, template }))
    ),
    [sections]
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [flatItems]);

  useEffect(() => {
    itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const selectTemplate = (template: SupportTemplate) => {
    if (resultsAreStale) return;
    onSelectTemplate(template);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (flatItems.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => (i + 1) % flatItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => (i - 1 + flatItems.length) % flatItems.length);
    } else if (e.key === 'Enter' && flatItems[highlightedIndex]?.template) {
      e.preventDefault();
      selectTemplate(flatItems[highlightedIndex].template);
    }
  };

  let flatIndex = 0;

  return (
    <div className="flex flex-col max-h-80">
      <div className="p-2 border-b border-gray-200 sticky top-0">
        <Input
          type="text"
          label={L.support.templates.search_placeholder}
          showLabel={false}
          placeholder={L.support.templates.search_placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          suffix={searching && searchQuery.trim() ? <Spinner size="xsmall" className="!w-auto p-0" /> : undefined}
        />
      </div>
      <div className="overflow-y-auto py-1">
        {visibleResults?.length === 0 && (
          <div className="px-3 py-4 text-sm text-gray-500 italic">
            {L.applicant.comms.no_templates_match_query.replace('$query', searchQuery.trim())}
          </div>
        )}
        {sections.map(([category, templates]) => (
          <div key={category ?? 'search'}>
            {category !== null && (
              <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {category === UNCATEGORIZED_KEY ? L.applicant.comms.uncategorized : category}
              </div>
            )}
            <ul className="m-0">
              {templates.map((template) => {
                const index = flatIndex++;
                const isHighlighted = index === highlightedIndex;
                return (
                  <li
                    key={`${category}:${template.id}`}
                    ref={el => { itemRefs.current[index] = el; }}
                    onClick={() => selectTemplate(template)}
                    className={cn(
                      'px-3 py-2',
                      resultsAreStale ? 'opacity-60 cursor-default' : 'cursor-pointer',
                      isHighlighted ? 'bg-gray-100' : 'hover:bg-gray-100'
                    )}
                  >
                    <div className="flex flex-row flex-wrap items-center gap-1 text-sm font-medium text-gray-900">
                      <div>{template.name}</div>
                      <div className="flex flex-row gap-1">
                        {Object.keys(template.lang_verification).map(
                          lang => <Badge key={lang} variant="info" className="text-xs">{lang.toUpperCase()}</Badge>
                        )}
                      </div>
                    </div>
                    {template.description && (
                      <div className="text-xs text-gray-500 mt-0.5">{template.description}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// The variant behind the previewed body. `body` is what gets inserted; `id` and
// `version` are carried so an insertion can be attributed to the exact versioned
// template that produced it.
type CachedVariant = { body: string; id: number; version: number };

export function SupportTemplatePreviewModal({ template, onClose, applicantInfo, onInsert, insertAs = 'plaintext', surface, caseId }: {
  template: SupportTemplate | null;
  onClose: () => void;
  applicantInfo?: InfoDict;
  // Receives the resolved body plus a reference to the template it came from, so
  // the consuming composer can attach it to the eventual send.
  onInsert: (text: string, insertion: SupportTemplateInsertion) => void;
  // The format the target composer works in. A RichTextEditor consumes richtext
  // (markdown, which may include HTML tags), so the body is inserted verbatim. A
  // plaintext composer (e.g. CommsWindow) would show any HTML tags as literal
  // text, so tags are stripped first. Defaults to 'plaintext' since the plain
  // composer is the common case.
  insertAs?: 'richtext' | 'plaintext';
  surface: SupportTemplateSurface;
  // Support case the insertion happened in, recorded on the audit entry so
  // template usage can be rolled up per case (the way `message_draft.case_id`
  // already does for AI drafts). Nothing on `message` carries a case reference,
  // so without this the only route from a template to a case is matching the
  // contact within the case's open window — and an insertion that never got sent
  // has no route at all. Undefined on the applicant comms surface, which has no
  // case.
  caseId?: string;
}) {
  const L = useLocalizedStrings();
  const getVariant = usePost('/api/admin_panel/support/templates/get_variant');
  const listGlobalConfig = usePost('/api/admin_panel/support/templates/global_config/list');
  // Analytics only, so failures must never surface over the insert.
  const logAuditEntry = usePost('/api/admin_panel/log_audit_entry', { suppressErrorDisplay: true });
  const [selectedLang, setSelectedLang] = useState<SupportedLanguage | null>(null);
  const [variantCache, setVariantCache] = useState<Record<string, CachedVariant>>({});
  const [loadingVariant, setLoadingVariant] = useState(false);
  const [globalConfigByLang, setGlobalConfigByLang] = useState<Record<string, { intro: string; outro: string }>>({});
  const [globalConfigLoaded, setGlobalConfigLoaded] = useState(false);

  const languages = useMemo(
    () => (template ? Object.keys(template.lang_verification ?? {}) as SupportedLanguage[] : []),
    [template],
  );

  const cacheKey = template && selectedLang ? `${template.id}::${selectedLang}` : null;
  const variant = cacheKey ? variantCache[cacheKey] : undefined;
  const variantBody = variant?.body;

  const messageBodyWithReplacements = useMemo(() => {
    if (!variantBody || !selectedLang) return;
    const globalConfig = globalConfigByLang[selectedLang];
    const combined = [globalConfig?.intro, variantBody, globalConfig?.outro]
      .filter(Boolean)
      .join('\n\n');
    return toMarkdownWithReplacements({
      lang: selectedLang,
      content: combined,
      info: applicantInfo ?? {},
      uid: applicantInfo?.uid as string | undefined,
      skipBidirectionalWrapper: true
    });
  }, [variantBody, applicantInfo, selectedLang, globalConfigByLang]);

  const hasBody = !loadingVariant && globalConfigLoaded && !!messageBodyWithReplacements;

  useEffect(() => {
    if (!template) {
      setSelectedLang(null);
      setVariantCache({});
      return;
    }
    setVariantCache({});
    setSelectedLang(languages[0] ?? null);
  }, [template, languages]);

  useAsyncEffect(async (signal) => {
    try {
      if (!template || !selectedLang) return;
      const key = `${template.id}::${selectedLang}`;
      if (variantCache[key] !== undefined) return;
      setLoadingVariant(true);
      const result = await getVariant({ settings: template.id, lang: selectedLang }, signal);
      if (signal.aborted) return;
      if (!result || 'error' in result) {
        return;
      }
      setVariantCache(c => ({ ...c, [key]: { body: result.body, id: result.id, version: result.version } }));
    } finally {
      if (!signal.aborted) setLoadingVariant(false);
    }
  }, [template?.id, selectedLang]);

  useAsyncEffect(async (signal) => {
    if (!template || globalConfigLoaded) return;
    try {
      const result = await listGlobalConfig({}, signal);
      if (signal.aborted) return;
      if (!result || 'error' in result) return;
      const byLang: Record<string, { intro: string; outro: string }> = {};
      for (const row of result) {
        byLang[row.lang] = { intro: row.intro, outro: row.outro };
      }
      setGlobalConfigByLang(byLang);
    }
    finally {
      if (!signal.aborted) setGlobalConfigLoaded(true);
    }
  }, [template, globalConfigLoaded]);

  function insertTemplate() {
    if (!messageBodyWithReplacements || !template || !variant || !selectedLang) return;
    // Template bodies are markdown that may also contain raw HTML (turndown's
    // `link-with-target` rule leaves `<a target=…>` alone), so stripping tags
    // on its own would hand the plaintext composer literal `##`/`**`/`-`
    // syntax. Rendering the markdown to HTML first turns every one of those
    // into a real element, which `stripHtmlTags` then flattens to text — and it
    // matches what the preview above already renders.
    const body = insertAs === 'plaintext'
      ? stripHtmlTags(markdownToHtml(messageBodyWithReplacements))
      : messageBodyWithReplacements;

    // Minted here rather than server-side so the insert needs no round-trip: the
    // same id goes to the composer (and from there onto the sent message's
    // metadata) and to the audit entry, which is what makes the
    // insertion -> send funnel an exact join instead of a ratio of two counts.
    const insertion: SupportTemplateInsertion = {
      insertionId: v4(),
      settingsId: template.id,
      variantId: variant.id,
    };
    onInsert(body, insertion);
    onClose();

    // Deliberately not awaited: an insert must not wait on (or be interrupted by)
    // an analytics write. A dropped entry shows up as a send with no matching
    // insertion, which is detectable, rather than as a broken insert.
    logAuditEntry({
      eventName: 'SUPPORT_TEMPLATE_INSERTED',
      applicant: applicantInfo?.uid as string | undefined,
      value: {
        ...insertion,
        lang: selectedLang,
        version: variant.version,
        templateName: template.name,
        surface,
        insertAs,
        ...(caseId ? { caseId } : {}),
      },
    }).catch((e) => console.warn('[support_template_insertion] failed to log audit entry', e));
  }

  return (
    <Modal
      open={!!template}
      onClose={onClose}
      title={template?.name || ''}
      size="lg"
    >
      {template && (
        <div className="flex flex-col gap-4">
          {template.description && (
            <div className="text-xs text-gray-500">{template.description}</div>
          )}
          <div className="flex flex-wrap gap-2">
            {languages.map(lang => {
              const isSelected = lang === selectedLang;
              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setSelectedLang(lang)}
                  className={cn(
                    'px-3 py-1 text-xs rounded-full ring-1 ring-inset transition-colors',
                    isSelected
                      ? 'bg-[var(--ak-identity-color-blue-50)] text-[var(--ak-identity-color-blue-4)] ring-[var(--ak-identity-color-blue-200)]'
                      : 'bg-[var(--ak-identity-color-off-white)] text-[var(--ak-identity-color-gray)] ring-[var(--ak-identity-color-beige)] hover:bg-gray-100',
                  )}
                >
                  {langToWord(lang)}
                </button>
              );
            })}
          </div>
          <Refreshing loading={loadingVariant} className="min-h-32">
            <div
              className="border border-gray-200 rounded-md p-3 prose prose-sm max-w-none min-h-32 overflow-y-auto"
              dangerouslySetInnerHTML={{
                __html: hasBody && !loadingVariant
                  ? markdownToHtml(messageBodyWithReplacements!)
                  : `<p class="text-gray-400 italic">${L.applicant.comms.no_template_variant_available}</p>`,
              }}
            />
          </Refreshing>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={onClose}>
              {L.applicant.comms.cancel_template_preview}
            </Button>
            <Button variant="primary" onClick={insertTemplate} isDisabled={!hasBody}>
              {L.applicant.comms.insert_template}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
